import type { WidgetPrechatField, WidgetPrechatForm } from "@/lib/widget-config";

export interface KnownIdentity {
  name?: string;
  email?: string;
  phone?: string;
}

export interface PrechatKnown {
  /** Known value per form field key (trimmed, non-empty). */
  values: Record<string, string>;
  /** Field keys whose value comes from a verified source: shown read-only. */
  locked: ReadonlySet<string>;
  /** Every REQUIRED field has a known value: the pre-chat is not needed. */
  covered: boolean;
}

const NONE: PrechatKnown = { values: {}, locked: new Set(), covered: false };

const clean = (v: string | null | undefined) => v?.trim() || undefined;

function identityValue(kind: WidgetPrechatField["kind"], id: KnownIdentity | null | undefined) {
  if (!id) return undefined;
  if (kind === "NAME") return clean(id.name);
  if (kind === "EMAIL") return clean(id.email);
  if (kind === "PHONE") return clean(id.phone);
  return undefined;
}

/**
 * What the widget already knows about an IDENTIFIED visitor (a host-identified or verified session, a
 * signed-in preview), mapped onto the operator's pre-chat fields. Sources, strongest first: the host's
 * identity prop, the BE profile (name / email / phone and, for custom fields, the value stored under
 * the same key), the session's display name (for NAME, when the profile does not expose it).
 *
 * - `covered`: every required field has a value, so no pre-chat is shown.
 * - `locked`: values from a VERIFIED source are read-only in the form: the host identity when it
 *   carries a proof (`hostVerified`), and profile / session values when the session itself is the
 *   verified identity (`lockServerSide`, e.g. the preview's signed-in operator).
 * An anonymous visitor (`active: false`) knows nothing: the pre-chat works as it always did.
 */
export function resolvePrechatKnown(opts: {
  form: WidgetPrechatForm | null;
  active: boolean;
  host: KnownIdentity | null | undefined;
  hostVerified: boolean;
  profile: KnownIdentity | null | undefined;
  profileCustom: Record<string, string>;
  session: { name?: string | null } | null | undefined;
  lockServerSide: boolean;
}): PrechatKnown {
  const { form } = opts;
  if (!opts.active || !form?.enabled || form.fields.length === 0) return NONE;
  const values: Record<string, string> = {};
  const locked = new Set<string>();
  for (const field of form.fields) {
    const fromHost = identityValue(field.kind, opts.host);
    const fromProfile = identityValue(field.kind, opts.profile);
    const fromSession = field.kind === "NAME" ? clean(opts.session?.name) : undefined;
    const custom = field.kind === "CUSTOM_TEXT" ? clean(opts.profileCustom[field.key]) : undefined;
    if (fromHost) {
      values[field.key] = fromHost;
      if (opts.hostVerified) locked.add(field.key);
    } else if (fromProfile || fromSession) {
      values[field.key] = (fromProfile ?? fromSession) as string;
      if (opts.lockServerSide) locked.add(field.key);
    } else if (custom) {
      values[field.key] = custom;
    }
  }
  const covered = form.fields.filter((f) => f.required).every((f) => !!values[f.key]);
  return { values, locked, covered };
}
